import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  Mail,
  Recycle,
  ShieldCheck,
} from "lucide-react";

import {
  completePasswordReset,
  requestPasswordReset,
  verifyPasswordResetCode,
} from "../lib/passwordRecovery";

const STEPS = {
  REQUEST: "request",
  VERIFY: "verify",
  RESET: "reset",
  DONE: "done",
};

const PasswordRecovery = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(STEPS.REQUEST);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] =
    useState("");

  const [showPassword, setShowPassword] =
    useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const passwordChecks = useMemo(
    () => ({
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /\d/.test(newPassword),
    }),
    [newPassword]
  );

  const passwordValid = Object.values(
    passwordChecks
  ).every(Boolean);

  const passwordsMatch =
    Boolean(confirmPassword) &&
    newPassword === confirmPassword;

  const clearFeedback = () => {
    setError("");
    setMessage("");
  };

  const getErrorMessage = (
    err,
    fallback
  ) =>
    err.response?.data?.message ||
    err.message ||
    fallback;

  const handleRequestCode = async (event) => {
    event.preventDefault();
    clearFeedback();

    const normalizedEmail =
      email.trim().toLowerCase();

    if (
      !normalizedEmail ||
      !/\S+@\S+\.\S+/.test(normalizedEmail)
    ) {
      setError(
        "Enter a valid email address."
      );
      return;
    }

    setLoading(true);

    try {
      await requestPasswordReset(
        normalizedEmail
      );

      setEmail(normalizedEmail);
      setCode("");
      setMessage(
        "If an account exists for that email, a verification code has been sent."
      );
      setStep(STEPS.VERIFY);
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          "The reset request could not be completed. Please try again."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event.preventDefault();
    clearFeedback();

    const cleanCode = code.trim();

    if (!/^\d{6}$/.test(cleanCode)) {
      setError(
        "Enter the 6-digit verification code."
      );
      return;
    }

    setLoading(true);

    try {
      const result =
        await verifyPasswordResetCode(
          email,
          cleanCode
        );

      if (!result?.resetToken) {
        throw new Error(
          "The server did not return a password reset token."
        );
      }

      setResetToken(result.resetToken);
      setMessage(
        "Email verified. Choose a new password."
      );
      setStep(STEPS.RESET);
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          "The verification code is invalid or has expired."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    clearFeedback();
    setLoading(true);

    try {
      await requestPasswordReset(email);

      setCode("");
      setMessage(
        "If an account exists for that email, a new verification code has been sent."
      );
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          "A new verification code could not be requested."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (
    event
  ) => {
    event.preventDefault();
    clearFeedback();

    if (!resetToken) {
      setError(
        "Your reset session is no longer valid. Request a new verification code."
      );
      setStep(STEPS.REQUEST);
      return;
    }

    if (!passwordValid) {
      setError(
        "Use at least 8 characters with uppercase, lowercase and a number."
      );
      return;
    }

    if (!passwordsMatch) {
      setError(
        "The passwords do not match."
      );
      return;
    }

    setLoading(true);

    try {
      await completePasswordReset(
        resetToken,
        newPassword
      );

      setResetToken("");
      setCode("");
      setNewPassword("");
      setConfirmPassword("");
      setMessage(
        "Your password has been changed successfully."
      );
      setStep(STEPS.DONE);
    } catch (err) {
      const status = err.response?.status;

      if (
        status === 401 ||
        status === 410
      ) {
        setResetToken("");
      }

      setError(
        getErrorMessage(
          err,
          "Your password could not be reset. The reset session may have expired."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const goBackOneStep = () => {
    clearFeedback();

    if (step === STEPS.VERIFY) {
      setCode("");
      setStep(STEPS.REQUEST);
      return;
    }

    if (step === STEPS.RESET) {
      setResetToken("");
      setCode("");
      setStep(STEPS.VERIFY);
      return;
    }

    navigate("/signin");
  };

  const title =
    step === STEPS.REQUEST
      ? "Reset your password"
      : step === STEPS.VERIFY
      ? "Verify your email"
      : step === STEPS.RESET
      ? "Create a new password"
      : "Password updated";

  const subtitle =
    step === STEPS.REQUEST
      ? "Enter the email linked to your DealSwapify account."
      : step === STEPS.VERIFY
      ? `Enter the 6-digit code sent to ${email}.`
      : step === STEPS.RESET
      ? "Choose a strong password you have not used before."
      : "You can now sign in using your new password.";

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="mb-8 flex items-center justify-center gap-2"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
            <Recycle className="h-6 w-6 text-primary-foreground" />
          </div>

          <span className="font-heading text-2xl font-bold text-foreground">
            Deal
            <span className="text-primary">
              Swapify
            </span>
          </span>
        </Link>

        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          {step !== STEPS.DONE && (
            <button
              type="button"
              onClick={goBackOneStep}
              disabled={loading}
              className="mb-5 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
          )}

          <div className="mb-6 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              {step === STEPS.DONE ? (
                <ShieldCheck className="h-5 w-5 text-primary" />
              ) : (
                <KeyRound className="h-5 w-5 text-primary" />
              )}
            </div>

            <div>
              <h1 className="font-heading text-xl font-bold text-foreground">
                {title}
              </h1>

              <p className="mt-1 text-sm leading-5 text-muted-foreground">
                {subtitle}
              </p>
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {message && (
            <div
              role="status"
              className="mb-4 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"
            >
              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{message}</span>
            </div>
          )}

          {step === STEPS.REQUEST && (
            <form
              onSubmit={handleRequestCode}
              className="space-y-4"
            >
              <div>
                <label
                  htmlFor="reset-email"
                  className="mb-1.5 block text-sm font-medium text-foreground"
                >
                  Email address
                </label>

                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

                  <input
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={(event) =>
                      setEmail(
                        event.target.value
                      )
                    }
                    autoComplete="email"
                    placeholder="you@example.com"
                    disabled={loading}
                    required
                    className="w-full rounded-lg border border-border bg-secondary py-2.5 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:shadow-[var(--glow-primary)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading
                  ? "Sending..."
                  : "Send verification code"}
              </button>
            </form>
          )}

          {step === STEPS.VERIFY && (
            <form
              onSubmit={handleVerifyCode}
              className="space-y-4"
            >
              <div>
                <label
                  htmlFor="reset-code"
                  className="mb-1.5 block text-sm font-medium text-foreground"
                >
                  Verification code
                </label>

                <input
                  id="reset-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(event) =>
                    setCode(
                      event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6)
                    )
                  }
                  maxLength={6}
                  placeholder="000000"
                  disabled={loading}
                  required
                  className="w-full rounded-lg border border-border bg-secondary px-4 py-3 text-center font-mono text-xl tracking-[0.35em] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                />
              </div>

              <button
                type="submit"
                disabled={
                  loading ||
                  code.length !== 6
                }
                className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:shadow-[var(--glow-primary)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading
                  ? "Verifying..."
                  : "Verify code"}
              </button>

              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading}
                className="w-full rounded-lg border border-border bg-background py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                Resend code
              </button>
            </form>
          )}

          {step === STEPS.RESET && (
            <form
              onSubmit={handleResetPassword}
              className="space-y-4"
            >
              <div>
                <label
                  htmlFor="new-password"
                  className="mb-1.5 block text-sm font-medium text-foreground"
                >
                  New password
                </label>

                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

                  <input
                    id="new-password"
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    value={newPassword}
                    onChange={(event) =>
                      setNewPassword(
                        event.target.value
                      )
                    }
                    autoComplete="new-password"
                    disabled={loading}
                    required
                    className="w-full rounded-lg border border-border bg-secondary py-2.5 pl-10 pr-10 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        (current) =>
                          !current
                      )
                    }
                    disabled={loading}
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>

                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <PasswordRule
                    passed={
                      passwordChecks.length
                    }
                    text="8+ characters"
                  />
                  <PasswordRule
                    passed={
                      passwordChecks.uppercase
                    }
                    text="Uppercase letter"
                  />
                  <PasswordRule
                    passed={
                      passwordChecks.lowercase
                    }
                    text="Lowercase letter"
                  />
                  <PasswordRule
                    passed={
                      passwordChecks.number
                    }
                    text="Number"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirm-password"
                  className="mb-1.5 block text-sm font-medium text-foreground"
                >
                  Confirm new password
                </label>

                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

                  <input
                    id="confirm-password"
                    type={
                      showConfirmPassword
                        ? "text"
                        : "password"
                    }
                    value={confirmPassword}
                    onChange={(event) =>
                      setConfirmPassword(
                        event.target.value
                      )
                    }
                    autoComplete="new-password"
                    disabled={loading}
                    required
                    className={`w-full rounded-lg border ${
                      confirmPassword &&
                      !passwordsMatch
                        ? "border-destructive"
                        : "border-border"
                    } bg-secondary py-2.5 pl-10 pr-10 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60`}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(
                        (current) =>
                          !current
                      )
                    }
                    disabled={loading}
                    aria-label={
                      showConfirmPassword
                        ? "Hide password"
                        : "Show password"
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>

                {confirmPassword && (
                  <p
                    className={`mt-1 text-xs ${
                      passwordsMatch
                        ? "text-primary"
                        : "text-destructive"
                    }`}
                  >
                    {passwordsMatch
                      ? "Passwords match."
                      : "Passwords do not match."}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={
                  loading ||
                  !passwordValid ||
                  !passwordsMatch
                }
                className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:shadow-[var(--glow-primary)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading
                  ? "Updating..."
                  : "Update password"}
              </button>
            </form>
          )}

          {step === STEPS.DONE && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    "/signin",
                    {
                      replace: true,
                    }
                  )
                }
                className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:shadow-[var(--glow-primary)]"
              >
                Sign in
              </button>

              <Link
                to="/"
                className="block w-full rounded-lg border border-border bg-background py-2.5 text-center text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                Return home
              </Link>
            </div>
          )}

          {step !== STEPS.DONE && (
            <div className="mt-5 text-center text-xs text-muted-foreground">
              Remembered your password?{" "}
              <Link
                to="/signin"
                className="font-medium text-primary hover:underline"
              >
                Sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PasswordRule = ({
  passed,
  text,
}) => (
  <span
    className={
      passed
        ? "text-primary"
        : "text-muted-foreground"
    }
  >
    {passed ? "✓" : "•"} {text}
  </span>
);

export default PasswordRecovery;
